import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import { CommentDto, Country } from "../../../../../../api/Api";
import { CommentsSection } from "../../../../../../components/lifecycle/commentsSection";
import { LifecycleDialog } from "../../../../../../components/lifecycle/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useCountryAssignees } from "../../../../dataHooks/countryQueries";
import { countryAttachmentApi } from "../../../countryAttachmentApi";
import { useCountryLifecycle } from "../../useCountryLifecycle";

type Props = {
  specificCountry: Country;
};

type FormData = {
  mandateApproval: string;
  mandateApprovalAssignee: string;
  signOffNote: string;
  comments: CommentDto[];
};

/**
 * Mandate approval, which behaves exactly as it does for counterparties and brokers. The two credit
 * checks (TSO and exchange) moved to their own Credit Risk lifecycle.
 */
export const Risk: FC<Props> = ({ specificCountry }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const lifecycle = specificCountry.riskLifecycle;

  const [formData, setFormData] = useState<FormData>({
    mandateApproval: lifecycle?.mandateApproval || "",
    mandateApprovalAssignee: lifecycle?.mandateApprovalAssignee || "",
    signOffNote: lifecycle?.signOffNote || "",
    comments: lifecycle?.comments || []
  });

  const handleClose = () => navigate(`/countries/country/${params.id}`);

  const {
    handleSave,
    handleComplete,
    isSaving,
    progressOverride,
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment
  } = useCountryLifecycle({
    country: specificCountry,
    lifecycle: "riskLifecycle",
    formData,
    setFormData,
    onClose: handleClose
  });

  const riskAssignees = useCountryAssignees("risk");

  const handleChange = (
    field: Exclude<keyof FormData, "comments">,
    value: string
  ) => setFormData((prev) => ({ ...prev, [field]: value }));

  // endsWith, not includes: the Credit Risk route ends in "creditrisk".
  const open = location.pathname.endsWith("/risk");

  const canComplete = Boolean(formData.mandateApproval);

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Risk"
      progressOverride={progressOverride}
      maxWidth="md"
      attachmentApi={countryAttachmentApi}
      actions={{
        onClose: handleClose,
        onSave: () => handleSave(),
        onComplete: () => handleComplete(),
        canComplete,
        isSaving
      }}
    >
      <Box sx={{ paddingTop: 2 }} gap={2} display="flex" flexDirection="column">
        <Box display="flex" gap={2}>
          <FormControl size="medium" required sx={{ flex: 1 }}>
            <InputLabel>Mandate approval</InputLabel>
            <Select
              label="Mandate approval"
              value={formData.mandateApproval}
              onChange={(e) => handleChange("mandateApproval", e.target.value)}
            >
              <MenuItem value="initiated">Initiated</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={riskAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              riskAssignees.data?.find(
                (a) => a.email === formData.mandateApprovalAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("mandateApprovalAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Mandate approval assignee"
                size="medium"
              />
            )}
          />
        </Box>

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) => handleChange("signOffNote", value)}
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};
